import { useState } from "react";
import { format, parseISO, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useAdminMonthly } from "@/hooks/use-admin-queries";
import { MonthlyAttendanceTable } from "@/components/attendance/monthly-attendance-table";
import { PersonMonthlyDrilldown } from "@/components/attendance/person-monthly-drilldown";
import { Button } from "@/components/ui/button";
import { todayIST } from "@/lib/timezone";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import type { MonthlyRow } from "@/lib/types";

function monthKey(d: Date): string {
  return format(d, "yyyy-MM");
}

export default function MonthlyAttendancePage() {
  const [cursor, setCursor] = useState(() =>
    startOfMonth(parseISO(todayIST()))
  );
  const [drilldown, setDrilldown] = useState<{
    row: MonthlyRow;
    tableLabel: string;
  } | null>(null);
  const month = monthKey(cursor);
  const adminPassword = getAdminPassword();
  const { data, isLoading, error } = useAdminMonthly(month);

  const year = cursor.getFullYear();
  const monthIndex = cursor.getMonth();

  const devoteesTable = data?.tables.find((t) => t.key === "devotees");
  const employeesTable = data?.tables.find((t) => t.key === "employees");

  async function handleExport() {
    if (!adminPassword) return;
    const res = await adminApi.exportMonthlyCsv(adminPassword, month);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Monthly Attendance</h2>
          <p className="text-sm text-muted-foreground">
            Summary counts and % — click a row for day-by-day drill-down
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-1 h-4 w-4" />
            Export Excel (CSV)
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCursor(new Date(year, monthIndex - 1, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="min-w-[140px] text-center font-medium">
            {format(cursor, "MMMM yyyy")}
          </p>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCursor(new Date(year, monthIndex + 1, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <Legend label="Present (P)" className="bg-emerald-100 text-emerald-800 border-emerald-200" />
        <Legend label="Late (L)" className="bg-amber-100 text-amber-900 border-amber-200" />
        <Legend label="Absent (A)" className="bg-red-100 text-red-800 border-red-200" />
        <Legend label="On leave (O)" className="bg-sky-100 text-sky-800 border-sky-200" />
        <Legend label="No data" className="bg-slate-100 text-slate-400 border-slate-200" />
        <Legend label="Holiday (H)" className="bg-violet-100 text-violet-800 border-violet-200" />
        <span className="text-muted-foreground">
          % = (P+L) ÷ accountable days (excludes H and pending)
        </span>
      </div>

      {isLoading && (
        <p className="text-muted-foreground">Loading monthly attendance…</p>
      )}
      {error && (
        <p className="text-red-600">Could not load monthly attendance.</p>
      )}

      {data && (
        <div className="space-y-10">
          <MonthlyAttendanceTable
            title={devoteesTable?.label ?? "Yoga & PDC"}
            subtitle="Non-employees — morning session per day"
            dates={data.dates}
            rows={devoteesTable?.rows ?? []}
            showCategory
            onRowClick={(row) =>
              setDrilldown({
                row,
                tableLabel: devoteesTable?.label ?? "Yoga & PDC",
              })
            }
          />
          <MonthlyAttendanceTable
            title={employeesTable?.label ?? "Employees"}
            subtitle="Shift blocks summarized per day"
            dates={data.dates}
            rows={employeesTable?.rows ?? []}
            onRowClick={(row) =>
              setDrilldown({
                row,
                tableLabel: employeesTable?.label ?? "Employees",
              })
            }
          />
        </div>
      )}

      <PersonMonthlyDrilldown
        open={drilldown !== null}
        onClose={() => setDrilldown(null)}
        row={drilldown?.row ?? null}
        tableLabel={drilldown?.tableLabel ?? ""}
        dates={data?.dates ?? []}
      />
    </div>
  );
}

function Legend({ label, className }: { label: string; className: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-4 w-4 rounded border ${className}`} />
      {label}
    </span>
  );
}
